import { useEffect, useRef, useState } from 'react';
import type { ArClaim, BilledWhy, StopReason } from '../types';
import type { StopInput } from '../store';
import {
  AR_TODAY_ISO,
  PAUSE_DEFAULT_DAYS,
  PAUSE_MAX_DAYS,
  isoLabel,
  isoPlusDays,
  money,
  moneyFor,
} from '../utils';
import { AR_PAYERS } from '../payers';
import { PMS } from '@/features/shared/pms';
import { MONEY_APPROVAL_NOTE, hasMoneyPermission } from '@/features/shared/permissions';

interface Props {
  claim: ArClaim;
  onCancel: () => void;
  onConfirm: (v: StopInput) => void;
}

/**
 * Stop following up (§5.1, Oct 22). Nothing is pre-selected. Pause, "billed to another insurance"
 * and "something else" are for everyone; "write it off" and "the patient will pay it" only show to
 * users with the money permission (D245: hidden, never shown and refused). Amounts can't be edited:
 * the whole open amount, or keep following up.
 */
export function StopDialog({ claim, onCancel, onConfirm }: Props) {
  const [reason, setReason] = useState<StopReason | null>(null);
  const [pauseIso, setPauseIso] = useState(isoPlusDays(AR_TODAY_ISO, PAUSE_DEFAULT_DAYS));
  const [otherPayer, setOtherPayer] = useState('');
  const [billedWhy, setBilledWhy] = useState<BilledWhy | null>(null);
  const [note, setNote] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const canMoney = hasMoneyPermission();

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onCancel]);

  const short = claim.payer.short;
  const first = claim.firstName;
  const m = moneyFor(claim);
  const patientCanPay = claim.patientPaysBasis !== undefined;
  const isMoneyReason = reason === 'write_off' || reason === 'patient_pays';
  const blockedBy = isMoneyReason ? m.closeBlockedBy : undefined;

  const minIso = isoPlusDays(AR_TODAY_ISO, 1);
  const maxIso = isoPlusDays(AR_TODAY_ISO, PAUSE_MAX_DAYS);
  const pauseOk = pauseIso >= minIso && pauseIso <= maxIso;

  const valid =
    reason !== null &&
    (reason !== 'pause' || pauseOk) &&
    (reason !== 'billed_elsewhere' || (otherPayer !== '' && billedWhy !== null)) &&
    (reason !== 'other' || note.trim() !== '');

  const primaryLabel =
    reason === 'write_off'
      ? `Stop and write off ${money(m.pmsPending)}`
      : reason === 'patient_pays'
        ? `Move ${money(m.pmsPending)} to the patient`
        : reason === 'pause' && pauseOk
          ? `Pause until ${isoLabel(pauseIso)}`
          : 'Stop following up';

  const choose = (r: StopReason) => { setReason(r); };

  const submit = () => {
    if (!valid) return;
    onConfirm({
      reason,
      ...(reason === 'pause' ? { pauseUntil: pauseIso } : {}),
      ...(reason === 'billed_elsewhere' && billedWhy ? { otherPayer, billedWhy } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const pmsLine =
    m.pmsPending !== m.elvaExpected
      ? `${PMS} shows ${money(m.pmsPending)} pending; ELVA expected ${money(m.elvaExpected)}`
      : `${PMS} shows ${money(m.pmsPending)} pending`;

  return (
    <div className="iar-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div ref={ref} className="iar-dialog" role="dialog" aria-modal="true" aria-labelledby="iar-stop-h">
        <h2 id="iar-stop-h" className="iar-dialog-title">Stop following up on {claim.patient}’s claim?</h2>
        <div className="iar-notice">{short} still owes {money(claim.balance)} on it.</div>

        <fieldset className="iar-radios">
          <legend className="iar-legend">Why?</legend>

          <label className="iar-opt">
            <input type="radio" name="iar-stop" checked={reason === 'pause'} onChange={() => { choose('pause'); }} />
            <span>Pause until a date</span>
          </label>
          {reason === 'pause' ? (
            <div className="iar-sub">
              <label className="iar-flabel" style={{ maxWidth: 220 }}>
                <span>Pause until</span>
                <input
                  className="iar-field"
                  type="date"
                  value={pauseIso}
                  min={minIso}
                  max={maxIso}
                  onChange={(e) => { setPauseIso(e.target.value); }}
                />
              </label>
              <div className="iar-meta">
                {pauseOk
                  ? `The claim moves to ELVA isn’t following up, and the next round runs on ${isoLabel(pauseIso)}. At most ${String(PAUSE_MAX_DAYS)} days.`
                  : `Pick a date between ${isoLabel(minIso)} and ${isoLabel(maxIso)} (at most ${String(PAUSE_MAX_DAYS)} days).`}
              </div>
            </div>
          ) : null}

          <label className="iar-opt">
            <input type="radio" name="iar-stop" checked={reason === 'billed_elsewhere'} onChange={() => { choose('billed_elsewhere'); }} />
            <span>It was billed to another insurance</span>
          </label>
          {reason === 'billed_elsewhere' ? (
            <div className="iar-sub">
              <label className="iar-flabel">
                <span>Which insurance?</span>
                <select className="iar-sel" value={otherPayer} onChange={(e) => { setOtherPayer(e.target.value); }}>
                  <option value="">Choose an insurance</option>
                  {AR_PAYERS.filter((p) => p.name !== claim.payer.name).map((p) => (
                    <option key={p.slug} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </label>
              <fieldset className="iar-radios">
                <legend className="iar-legend">What happened?</legend>
                <label className="iar-opt">
                  <input type="radio" name="iar-why" checked={billedWhy === 'wrong_insurance'} onChange={() => { setBilledWhy('wrong_insurance'); }} />
                  <span>It was sent to the wrong insurance</span>
                </label>
                <label className="iar-opt">
                  <input type="radio" name="iar-why" checked={billedWhy === 'coordination'} onChange={() => { setBilledWhy('coordination'); }} />
                  <span>The other insurance is the secondary (or the primary)</span>
                </label>
                <label className="iar-opt">
                  <input type="radio" name="iar-why" checked={billedWhy === 'forwarded'} onChange={() => { setBilledWhy('forwarded'); }} />
                  <span>{short} forwarded it to them</span>
                </label>
              </fieldset>
              <div className="iar-meta">{short}’s {money(claim.balance)} still counts as owed until ELVA finds the other claim.</div>
            </div>
          ) : null}

          {canMoney ? (
            <>
              <label className="iar-opt">
                <input type="radio" name="iar-stop" checked={reason === 'write_off'} onChange={() => { choose('write_off'); }} />
                <span>Write it off: we won’t collect it</span>
              </label>
              <label className={`iar-opt${patientCanPay ? '' : ' disabled'}`}>
                <input
                  type="radio"
                  name="iar-stop"
                  disabled={!patientCanPay}
                  checked={reason === 'patient_pays'}
                  onChange={() => { choose('patient_pays'); }}
                />
                <span>{first} will pay it</span>
              </label>
              {!patientCanPay ? (
                <div className="iar-optnote">Only after {short} decides the claim. ELVA can’t bill {first} for what {short} owes.</div>
              ) : null}
            </>
          ) : null}

          <label className="iar-opt">
            <input type="radio" name="iar-stop" checked={reason === 'other'} onChange={() => { choose('other'); }} />
            <span>Something else</span>
          </label>

          {!canMoney ? <div className="iar-optnote">{MONEY_APPROVAL_NOTE}</div> : null}
        </fieldset>

        {isMoneyReason ? (
          <div className="iar-amounts" aria-label="Amounts, read-only">
            <div>{pmsLine}</div>
            {reason === 'write_off' ? (
              <div>{first}’s balance for this visit stays {money(m.patientShare)}</div>
            ) : (
              <div>
                {first} would owe {money(m.patientShare + m.pmsPending)} for this visit: the {money(m.patientShare)} share plus the {money(m.pmsPending)} {short} hasn’t paid. In network, never more than the contracted fee less what was paid.
              </div>
            )}
          </div>
        ) : null}

        <label className="iar-flabel">
          <span>Note <span className="iar-meta">({reason === 'other' ? 'required' : 'optional'})</span></span>
          <input className="iar-field" value={note} onChange={(e) => { setNote(e.target.value); }} />
        </label>

        {reason === 'write_off' ? (
          <div className="iar-meta">ELVA records the write-off in {PMS}, keeps it under Written off, and counts it in ELVA’s work.</div>
        ) : reason === 'patient_pays' ? (
          <div className="iar-meta">
            ELVA moves {money(m.pmsPending)} to {claim.patient}’s balance in {PMS} and logs it. Collecting it is ELVA Core’s job.
          </div>
        ) : reason === 'pause' || reason === 'other' || reason === 'billed_elsewhere' ? (
          <div className="iar-meta">ELVA logs this in ELVA’s work. The claim still counts as owed.</div>
        ) : null}

        {blockedBy ? (
          <div className="iar-notice" role="status">
            ELVA can’t close this claim while {blockedBy}. Keep following up, or adjust it in {PMS} yourself.
          </div>
        ) : null}

        <div className="iar-dialog-actions">
          {!blockedBy ? (
            <button type="button" className="iar-btn iar-btn-primary iar-btn-lg" disabled={!valid} onClick={submit}>
              {primaryLabel}
            </button>
          ) : null}
          <button type="button" className="iar-btn iar-btn-lg" onClick={onCancel}>{blockedBy ? 'Keep following up' : 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}
